/**
 * <TutorSessionReplay> — l'onglet « Fil » du centre : la séance d'un élève, en un seul fil (organism).
 *
 * **Une forme pour tout** (Wajih, 05/11 : « la forme de chaque élément du fil devait être adaptée pour être plus
 * harmonieuse à l'œil ») : chaque moment est une ligne — l'heure, son point, le fait en une ligne, au plus une ligne
 * grise —, sans carte. Le point est celui que la tuile montre pour le même moment (`<ThreadDot>`, sa forme et sa
 * couleur) ; une piste les relie à gauche, colorée par le mode (Dialogue, Concentré), hachurée là où l'élève était sorti
 * de l'appli (une sortie d'au moins une minute a sa ligne, une plus courte reste une hachure). Ce que le masteur a tapé
 * garde une bulle discrète, teintée, jamais l'aplat de l'univers.
 *
 * **Le détail se déplie sur place**, dans la même typographie : les propositions d'une demande (survolées : leur aperçu
 * au centre ; touchées : leur relecture), les étapes d'une activité (« Ouvrir » la montre au centre), le texte du
 * champion. Les gestes qui décident (ajouter, envoyer, écarter) restent dans « À traiter » ; ici, une ligne dit l'issue
 * (« ajoutée à sa liste », « à traiter »).
 *
 * **Les repères sont des séparateurs** : « Depuis ton dernier passage », et « Historique de ce navigateur depuis
 * 08:14 » quand la séance a commencé ailleurs (son explication au toucher). Le fil s'ouvre sur maintenant ; un point
 * touché dans la tuile (`focus`) l'amène à sa ligne, éclairée.
 */
import React, { useEffect, useRef, useState } from "react"
import { Pressable, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { endSentence } from "../../../ports/primary/ui/formatters/textFormatter"
import { tutorTraceText } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { type UITutorSessionEntry, type UITutorSessionRibbon } from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ScrollView } from "../../atoms/ScrollView"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useFollowEnd } from "../../hooks/useFollowEnd"
import { SessionStartedHere } from "../../molecules/SessionStartedHere"
import { MODE_COLOR, ThreadDot, ThreadHatch } from "../../molecules/ThreadDot"
import { TutorSessionEntry, type TutorSessionEntryProps } from "../TutorSessionEntry"
import { ProposalPeekRow } from "../TutorSessionEntry/ProposalPeekRow"
import { lineContaining, lineCopy, railPieces, threadLines, type ThreadLine } from "./threadLines"

export type TutorSessionReplayProps = {
    helper: ChampionName
    firstname: string
    /** Le fil de la tuile : les points, le mode et les sorties de la piste. Absent : les lignes seules. */
    thread?: UITutorSessionRibbon | null
    /** L'historique, à l'heure (`tutorSessionTimeline`). */
    history: UITutorSessionEntry[]
    /** La séance a commencé ailleurs : l'instant (ISO) d'arrivée sur cet appareil. */
    startedHere?: string
    /** « Ouvrir » une activité au centre. */
    onOpenActivity?: (activityId: string) => void
    /** Une proposition touchée : sa relecture au centre (`review`). */
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    onPeek?: TutorSessionEntryProps["onPeek"]
    peekId?: TutorSessionEntryProps["peekId"]
    /**
     * Un geste dans la tuile : défiler jusqu'à la ligne d'un point (`eventId`) et l'éclairer, ou jusqu'à maintenant.
     * `nonce` change à chaque geste, même sur le même point.
     */
    focus?: { eventId?: string; nonce: number } | null
    testID?: string
}

const TEXTS = {
    empty: (firstname: string) => endSentence(`Rien encore avec ${firstname}`),
    open: "Ouvrir",
}

/** La hauteur de la première ligne d'une rangée : l'heure et le point s'y centrent. */
const HEAD = 20
const DOT = 10
/** Un fait au bord de l'écran se lit sous le haut de la zone, pas collé à lui. */
const SCROLL_MARGIN = 16
/** À moins de cette distance du bas, le fil suit ce qui arrive. */
const END_SLACK = 24

export function TutorSessionReplay(props: TutorSessionReplayProps) {
    const { helper, firstname, thread, history, startedHere, onOpenActivity, onGeneration, onPeek, peekId } = props
    const { focus, testID } = props
    const helperName = CHAMPIONS[helper].label
    // Pas de mémo : `thread` est refait à chaque rendu de la salle (le fil finit à maintenant), il ne tiendrait jamais.
    const items = threadLines(history, thread)

    // État d'interface : les lignes dépliées, la ligne éclairée par un geste de la tuile, et ce qu'il faut encore faire
    // défiler une fois la mise en page connue.
    const [open, setOpen] = useState<Record<string, boolean>>({})
    const [lit, setLit] = useState<string | null>(null)
    const scrollRef = useRef<ScrollView>(null)
    const rowY = useRef<Record<string, number>>({})
    const pending = useRef<string | null>("end")
    const { atEnd, followEnd, onScroll } = useFollowEnd(scrollRef, END_SLACK)
    const handled = useRef<number | null>(null)

    const scrollToRow = (id: string) => {
        const y = rowY.current[id]
        if (y == null) return false
        scrollRef.current?.scrollTo({ y: Math.max(0, y - SCROLL_MARGIN), animated: true })
        return true
    }

    useEffect(() => {
        if (focus == null || handled.current === focus.nonce) return
        handled.current = focus.nonce
        const target = focus.eventId != null ? (lineContaining(items, focus.eventId)?.id ?? null) : null
        setLit(target)
        if (target == null) {
            pending.current = null
            scrollRef.current?.scrollToEnd({ animated: true })
            return
        }
        // Le fil ne suit plus le bas : la ligne dépliée grandit le contenu, qui sinon redescendrait au bas.
        atEnd.current = false
        setOpen((current) => ({ ...current, [target]: true }))
        pending.current = scrollToRow(target) ? null : target
    }, [focus, items, atEnd])

    const onRowLayout = (id: string) => (event: LayoutChangeEvent) => {
        rowY.current[id] = event.nativeEvent.layout.y
        if (pending.current === id && scrollToRow(id)) pending.current = null
    }
    const onContentSizeChange = () => {
        if (pending.current === "end") pending.current = null
        else if (pending.current != null || !atEnd.current) return
        followEnd()
    }

    return (
        <ScrollView
            ref={scrollRef}
            contentContainerStyle={styles.scroll}
            onContentSizeChange={onContentSizeChange}
            onScroll={onScroll}
            scrollEventThrottle={64}
            testID={testID}
        >
            {startedHere != null && (
                <SessionStartedHere at={startedHere} helperName={helperName} style={styles.marker} />
            )}
            {items.length === 0 && startedHere == null && (
                <Text size="text-sm" color="text-muted">
                    {TEXTS.empty(firstname)}
                </Text>
            )}
            {items.map((item) => {
                if (item.kind === "since") {
                    return (
                        <View key={item.id} style={styles.marker}>
                            <TutorSessionEntry entry={item} helper={helper} />
                        </View>
                    )
                }
                const { minute, next } = item
                return (
                    <ThreadRow
                        key={item.id}
                        line={item}
                        helperName={helperName}
                        rail={
                            thread != null && minute != null && next != null ? railPieces(thread, minute, next) : null
                        }
                        open={open[item.id] === true}
                        lit={lit === item.id}
                        onToggle={() => setOpen((current) => ({ ...current, [item.id]: current[item.id] !== true }))}
                        onLayout={onRowLayout(item.id)}
                        onOpenActivity={onOpenActivity}
                        onGeneration={onGeneration}
                        onPeek={onPeek}
                        peekId={peekId}
                    />
                )
            })}
        </ScrollView>
    )
}

type ThreadRowProps = Pick<TutorSessionReplayProps, "onOpenActivity" | "onGeneration" | "onPeek" | "peekId"> & {
    line: ThreadLine
    helperName: string
    rail: ReturnType<typeof railPieces> | null
    open: boolean
    lit: boolean
    onToggle: () => void
    onLayout: (event: LayoutChangeEvent) => void
}

/** Une ligne du fil : l'heure, le point et sa part de piste, le fait, sa ligne grise, et son détail déplié. */
function ThreadRow(props: ThreadRowProps) {
    const { line, helperName, rail, open, lit, onToggle, onLayout, onOpenActivity, onGeneration, onPeek, peekId } =
        props
    const copy = lineCopy(line, helperName)
    const entry = line.kind === "entry" ? line.entry : null
    const activityId = entry?.kind === "activity" ? entry.activityId : undefined
    const lines = open ? 0 : 1
    // La ligne n'est pas un bouton qui en contient d'autres (« Ouvrir », les propositions : un `<button>` dans un
    // `<button>` au web) : seul le fait et sa ligne grise déplient, les gestes sont à côté.
    return (
        <View onLayout={onLayout} style={styles.row(lit)}>
            <View style={styles.time}>
                <Text size="text-xxs" color="text-muted">
                    {formatTime(line.at)}
                </Text>
            </View>
            <View style={styles.rail}>
                <View style={styles.dotBox}>
                    <ThreadDot kind={line.dot} size={DOT} />
                </View>
                <Rail pieces={rail} />
            </View>
            <Stack gap="1" style={styles.content}>
                <Row gap="2" align="start">
                    <Pressable onPress={onToggle} accessibilityRole="button" aria-expanded={open} style={styles.flex}>
                        <Stack gap="1">
                            {entry?.kind === "working" ? (
                                <View style={styles.head}>
                                    <WaitingText size="text-sm" align="start">
                                        {copy.head}
                                    </WaitingText>
                                </View>
                            ) : (
                                <Text
                                    size="text-sm"
                                    color={copy.quiet === true ? "text-muted" : "text-strong"}
                                    numberOfLines={lines}
                                    style={[styles.head, copy.quiet === true && styles.quiet]}
                                >
                                    {copy.head}
                                </Text>
                            )}
                            {copy.sub != null && copy.sub !== "" && (
                                <Text size="text-xs" color="text-muted" numberOfLines={lines}>
                                    {copy.sub}
                                </Text>
                            )}
                            {copy.message != null && (
                                <View style={styles.message(lit)}>
                                    <Text size="text-sm" color="text-primary" numberOfLines={open ? 0 : 2}>
                                        {copy.message}
                                    </Text>
                                </View>
                            )}
                        </Stack>
                    </Pressable>
                    {activityId != null && onOpenActivity != null && (
                        <TextButton label={TEXTS.open} size="sm" onPress={() => onOpenActivity(activityId)} />
                    )}
                </Row>
                {open && entry != null && (
                    <Detail entry={entry} onGeneration={onGeneration} onPeek={onPeek} peekId={peekId} />
                )}
            </Stack>
        </View>
    )
}

/** Le détail d'une ligne, dans la typographie du fil. */
function Detail(
    props: Pick<ThreadRowProps, "onGeneration" | "onPeek" | "peekId"> & {
        entry: Exclude<UITutorSessionEntry, { kind: "since" }>
    }
) {
    const { entry, onGeneration, onPeek, peekId } = props
    switch (entry.kind) {
        case "helper":
            return entry.title != null ? (
                <Text size="text-sm" color="text-primary">
                    {entry.text}
                </Text>
            ) : null
        case "progress":
            return (
                <Stack gap="0">
                    {entry.steps.map((step) => (
                        <Text
                            key={step.label}
                            size="text-xs"
                            weight={step.state === "active" ? "semibold" : "regular"}
                            color={step.state === "active" ? "text-strong" : "text-muted"}
                        >
                            {step.label}
                        </Text>
                    ))}
                </Stack>
            )
        case "ready":
            return (
                <Stack gap="0">
                    {entry.proposals.map((proposal, position) => (
                        <ProposalPeekRow
                            key={proposal.id}
                            proposal={proposal}
                            peekId={peekId}
                            onPeek={onPeek}
                            onPress={onGeneration != null ? () => onGeneration(entry.id, "review") : undefined}
                            style={styles.proposal}
                        >
                            <Text
                                size="text-xs"
                                color={proposal.status === "ready" ? "text-primary" : "text-muted"}
                                numberOfLines={1}
                                style={styles.flex}
                            >
                                {[String(position + 1), proposal.label, proposal.title]
                                    .filter((part) => part !== "")
                                    .join(" · ")}
                            </Text>
                        </ProposalPeekRow>
                    ))}
                </Stack>
            )
        case "result":
            return (
                <Stack gap="0">
                    {entry.activities.map((activity, position) => (
                        <Text key={String(activity.id)} size="text-xs" color="text-primary" numberOfLines={1}>
                            {`${position + 1} · ${activity.title ?? ""}`}
                        </Text>
                    ))}
                </Stack>
            )
        case "proposal":
            return (
                <Stack gap="0">
                    <Text size="text-xs" color="text-primary">
                        {entry.reason}
                    </Text>
                    <Text size="text-xs" color="text-muted">
                        {[entry.meta, entry.validWhile].filter((part) => part != null).join(" · ")}
                    </Text>
                </Stack>
            )
        case "activity":
            return (
                <Stack gap="0">
                    {entry.traces.map((trace) => (
                        <Row key={trace.id} gap="2" align="start">
                            <Text size="text-xxs" color="text-muted" style={styles.traceTime}>
                                {formatTime(trace.at)}
                            </Text>
                            <Text
                                size="text-xs"
                                color={trace.quiet === true ? "text-muted" : "text-primary"}
                                style={styles.flex}
                            >
                                {tutorTraceText(trace)}
                            </Text>
                        </Row>
                    ))}
                </Stack>
            )
        default:
            return null
    }
}

/**
 * La part de piste d'une ligne, jusqu'à la suivante : le mode à sa couleur, au prorata du temps ; une sortie de l'appli
 * hachurée jusqu'à son retour, un trait gris court pour une sortie trop brève pour sa ligne.
 */
function Rail(props: { pieces: ReturnType<typeof railPieces> | null }) {
    const { pieces } = props
    return (
        <View style={styles.track}>
            {pieces?.modes.map((piece) => (
                <View
                    key={`${piece.mode}-${piece.top}`}
                    style={styles.mode(`${piece.top * 100}%`, `${piece.height * 100}%`, piece.mode)}
                />
            ))}
            {pieces?.out.map((piece) => (
                <View key={`out-${piece.top}`} style={styles.span(`${piece.top * 100}%`, `${piece.height * 100}%`)}>
                    <ThreadHatch />
                </View>
            ))}
            {pieces?.away.map((at) => (
                <View key={`away-${at}`} style={styles.blip(`${at * 100}%`)} />
            ))}
        </View>
    )
}

const TRACK = 4
const BLIP = 6

const styles = StyleSheet.create((theme) => ({
    scroll: { padding: theme.spacing["3"] },
    marker: { paddingVertical: theme.spacing["1"] },
    row: (lit: boolean) => ({
        flexDirection: "row" as const,
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        backgroundColor: lit ? theme.colors["surface-subtle"] : undefined,
    }),
    time: { width: 36, height: HEAD, justifyContent: "center" as const, alignItems: "flex-end" as const },
    rail: { width: DOT + 4, alignItems: "center" as const },
    dotBox: { height: HEAD, justifyContent: "center" as const, alignItems: "center" as const },
    track: {
        flex: 1,
        width: TRACK,
        minHeight: theme.spacing["2"],
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors.border,
    },
    mode: (top: `${number}%`, height: `${number}%`, mode: "dialogue" | "focused") => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        top,
        height,
        backgroundColor: theme.colors[MODE_COLOR[mode]],
    }),
    span: (top: `${number}%`, height: `${number}%`) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        top,
        height,
    }),
    blip: (top: `${number}%`) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        top,
        height: BLIP,
        marginTop: -BLIP / 2,
        backgroundColor: theme.colors["text-muted"],
    }),
    content: { flex: 1, minWidth: 0, paddingBottom: theme.spacing["3"] },
    head: { minHeight: HEAD, justifyContent: "center" as const },
    flex: { flex: 1, minWidth: 0 },
    quiet: { fontStyle: "italic" as const },
    /**
     * Ce que le masteur a tapé : une bulle discrète, la teinte des encarts, jamais l'aplat de l'univers ; blanche sur la
     * ligne éclairée, qui prend cette teinte.
     */
    message: (lit: boolean) => ({
        alignSelf: "flex-start" as const,
        maxWidth: "100%",
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.md,
        backgroundColor: lit ? theme.colors.surface : theme.colors["surface-subtle"],
    }),
    proposal: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        paddingVertical: 2,
    },
    traceTime: { width: 32 },
}))
